import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import de from './de.json'
import en from './en.json'
import uk from './uk.json'
import ru from './ru.json'
import ar from './ar.json'
import tr from './tr.json'
import fr from './fr.json'
import es from './es.json'
import pl from './pl.json'
import bg from './bg.json'
import cs from './cs.json'
import ro from './ro.json'
import fa from './fa.json'
import ku from './ku.json'
import ckb from './ckb.json'

export const LANGUAGES = ['de', 'en', 'uk', 'ru', 'ar', 'tr', 'fr', 'es', 'pl', 'bg', 'cs', 'ro', 'fa', 'ku', 'ckb'] as const
export type Language = (typeof LANGUAGES)[number]

export const DEFAULT_LANGUAGE: Language = 'de'

export const ENDONYMS: Record<Language, string> = {
  de: 'Deutsch',
  en: 'English',
  uk: 'Українська',
  ru: 'Русский',
  ar: 'العربية',
  tr: 'Türkçe',
  fr: 'Français',
  es: 'Español',
  pl: 'Polski',
  bg: 'Български',
  cs: 'Čeština',
  ro: 'Română',
  fa: 'فارسی',
  ku: 'Kurdî (Kurmancî)',
  ckb: 'کوردیی ناوەندی',
}

export const NAMESPACES = ['portal', 'vergabeStaff'] as const

type Locale = { portal: typeof de.portal; vergabeStaff?: typeof de.vergabeStaff }

export const resources: Record<Language, Locale> = { de, en, uk, ru, ar, tr, fr, es, pl, bg, cs, ro, fa, ku, ckb }

const RTL: ReadonlySet<Language> = new Set<Language>(['ar', 'fa', 'ckb'])

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value)
}

export function directionOf(language: string): 'rtl' | 'ltr' {
  return isLanguage(language) && RTL.has(language) ? 'rtl' : 'ltr'
}

export function applyDocumentLanguage(language: string): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.lang = language
  root.dir = directionOf(language)
}

let initialised = false

export function initI18n(): typeof i18next {
  if (initialised) return i18next
  initialised = true
  void i18next.use(initReactI18next).init({
    resources,
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: [...LANGUAGES],
    ns: [...NAMESPACES],
    defaultNS: 'portal',
    keySeparator: false,
    interpolation: { escapeValue: false },
    initAsync: false,
    returnNull: false,
    react: { useSuspense: false },
  })
  i18next.on('languageChanged', applyDocumentLanguage)
  applyDocumentLanguage(i18next.language ?? DEFAULT_LANGUAGE)
  return i18next
}

export function currentLanguage(): Language {
  const language = initI18n().language
  return isLanguage(language) ? language : DEFAULT_LANGUAGE
}

export async function setLanguage(language: Language): Promise<void> {
  await initI18n().changeLanguage(language)
}
